import { DiscountInputType } from '@/renderer/types/order';
import { AddDiscountToOrderModalProps } from '@/renderer/types/sales';
import { apiError } from '@/renderer/util/notify';
import { Loader2, Percent, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../common';

export const AddDiscountToOrderModal: React.FC<
  AddDiscountToOrderModalProps
> = ({
  isOpen,
  orderTotal,
  existingDiscount,
  onClose,
  onSubmit,
  onRemove,
  isSubmitting = false,
  isRemoving = false,
}) => {
  const [type, setType] = useState<DiscountInputType>('AMOUNT');
  const [valueInput, setValueInput] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setType('AMOUNT');
    setValueInput('');
    setNotes('');
    setError(null);
  }, [isOpen]);

  const value = Number(valueInput || 0);
  const computedDiscount = useMemo(() => {
    if (!value || Number.isNaN(value)) return 0;
    if (type === 'PERCENTAGE') {
      return (orderTotal * value) / 100;
    }
    return value;
  }, [orderTotal, type, value]);

  const remainingAfterAllDiscounts = Math.max(orderTotal - computedDiscount, 0);

  if (!isOpen) return null;

  const validate = (): boolean => {
    if (!value || Number.isNaN(value) || value <= 0) {
      apiError('Enter a valid discount value');
      return false;
    }

    if (type === 'PERCENTAGE' && value >= 100) {
      apiError('Discount percentage must be less than 100');
      return false;
    }

    if (computedDiscount >= orderTotal) {
      apiError('Discount amount must be less than order total');
      return false;
    }

    if (notes.trim().length < 5) {
      apiError('Please provide a reason for the discount');
      return false;
    }

    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    await onSubmit({
      notes,
      type,
      value,
    });
  };

  const handleRemove = async () => {
    if (isSubmitting || isRemoving || existingDiscount <= 0) return;
    await onRemove();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        onClick={isSubmitting || isRemoving ? undefined : onClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isSubmitting && !isRemoving) onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close discount modal"
      />

      <div className="relative mx-4 w-full max-w-xl overflow-hidden rounded-md bg-white text-black shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-black">Add Discount</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting || isRemoving}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60"
            aria-label="Close modal"
          >
            <X size={22} />
          </button>
        </div>

        <div className="space-y-4 px-6 pb-6 pt-5">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setType('AMOUNT')}
              disabled={isSubmitting}
              className={`h-10 rounded border text-xs font-semibold uppercase tracking-wide transition-colors ${
                type === 'AMOUNT'
                  ? 'border-primary bg-primary text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              By Amount
            </button>
            <button
              type="button"
              onClick={() => setType('PERCENTAGE')}
              disabled={isSubmitting}
              className={`h-10 rounded border text-xs font-semibold uppercase tracking-wide transition-colors ${
                type === 'PERCENTAGE'
                  ? 'border-primary bg-primary text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              By Percentage
            </button>
          </div>

          <div>
            <p className="mb-1 text-sm font-semibold text-gray-800">
              {type === 'AMOUNT' ? 'Discount Amount' : 'Discount Percentage'}
            </p>
            <div className="relative">
              {type === 'PERCENTAGE' ? (
                <Percent
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                />
              ) : null}
              <input
                id="discount-value"
                type="number"
                min={0}
                step={type === 'AMOUNT' ? '1' : '0.01'}
                value={valueInput}
                onChange={(event) => setValueInput(event.target.value)}
                disabled={isSubmitting}
                aria-label={
                  type === 'AMOUNT' ? 'Discount amount' : 'Discount percentage'
                }
                placeholder={type === 'AMOUNT' ? 'Enter amount' : 'Enter %'}
                className={`h-10 w-full rounded border border-gray-300 px-3 text-sm text-gray-900 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300 ${
                  type === 'PERCENTAGE' ? 'pl-9' : ''
                }`}
              />
            </div>
          </div>

          <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <p>
              Order total: <strong>{orderTotal.toLocaleString()}</strong>
            </p>
            <p>
              Existing discounts:{' '}
              <strong>{existingDiscount.toLocaleString()}</strong>
            </p>
            <p>
              New discount: <strong>{computedDiscount.toLocaleString()}</strong>
            </p>
            <p>
              Remaining total:{' '}
              <strong>{remainingAfterAllDiscounts.toLocaleString()}</strong>
            </p>
          </div>

          <div className="space-y-2">
            <p className="mb-1 text-sm font-semibold text-gray-800">
              Notes (optional)
            </p>
            <textarea
              id="discount-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={isSubmitting}
              aria-label="Discount notes"
              rows={4}
              placeholder="Why is this discount being applied?"
              className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
            />
          </div>

          {error ? (
            <p className="text-sm font-medium text-red-600">{error}</p>
          ) : null}

          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleRemove}
              disabled={isSubmitting || isRemoving || existingDiscount <= 0}
              className="h-10 border-rose-500 text-xs font-semibold uppercase tracking-wide text-rose-600 hover:bg-rose-500 hover:text-white"
            >
              {isRemoving ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Removing
                </>
              ) : (
                'Remove Discount'
              )}
            </Button>

            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || isRemoving}
              className="h-10 text-xs font-semibold uppercase tracking-wide"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Applying
                </>
              ) : (
                'Apply Discount'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
